// ABOUTME: Formats Pure query strings with semantic line breaks and indentation.
// ABOUTME: Splits at top-level -> operators so each chained method is on its own line.

/**
 * Formats a Pure query with semantic line breaks and indentation.
 * Splits at top-level -> operators so each chained method is on its own line.
 * For long method calls, breaks arguments across lines.
 */
export function formatPureQuery(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return trimmed;

  const segments = splitAtTopLevelArrows(trimmed);

  // Short queries with a single -> stay on one line
  if (segments.length <= 1) return trimmed;
  if (segments.length === 2 && trimmed.length <= 80) return trimmed;

  const MAX_LINE = 72;
  const lines: string[] = [];

  for (let i = 0; i < segments.length; i++) {
    let seg = segments[i].trim();

    // Strip trailing } from the last segment — we'll add it on its own line
    const isLast = i === segments.length - 1;
    let trailingBrace = '';
    if (isLast && seg.endsWith('}')) {
      trailingBrace = '}';
      seg = seg.slice(0, -1).trimEnd();
    }

    if (i === 0) {
      lines.push(seg);
    } else {
      const indented = '  ' + seg;
      if (indented.length > MAX_LINE) {
        lines.push(breakMethodArgs(seg, 2));
      } else {
        lines.push(indented);
      }
    }

    if (trailingBrace) {
      lines.push(trailingBrace);
    }
  }

  return lines.join('\n');
}

/**
 * Splits a Pure query string at top-level -> operators.
 * Top-level means depth == 1 (inside the outer {|...} wrapper).
 * Nested arrows like $y->sum() inside agg() are not split.
 */
function splitAtTopLevelArrows(query: string): string[] {
  const segments: string[] = [];
  let current = '';
  let depth = 0;
  let inString = false;
  let stringChar = '';

  for (let i = 0; i < query.length; i++) {
    const ch = query[i];

    if (inString) {
      current += ch;
      if (ch === stringChar && query[i - 1] !== '\\') {
        inString = false;
      }
      continue;
    }

    if (ch === "'" || ch === '"') {
      inString = true;
      stringChar = ch;
      current += ch;
      continue;
    }

    if (ch === '(' || ch === '[' || ch === '{') depth++;
    if (ch === ')' || ch === ']' || ch === '}') depth--;

    // Split at -> when depth == 1 (inside the outer {| })
    if (depth === 1 && ch === '-' && i + 1 < query.length && query[i + 1] === '>') {
      segments.push(current);
      current = '->';
      i++; // skip '>'
      continue;
    }

    current += ch;
  }

  if (current) segments.push(current);
  return segments;
}

/**
 * Breaks a long method call's arguments across lines.
 */
function breakMethodArgs(segment: string, baseIndent: number): string {
  const indent = ' '.repeat(baseIndent);
  const argIndent = ' '.repeat(baseIndent + 2);

  const parenIdx = segment.indexOf('(');
  if (parenIdx === -1) return indent + segment;

  const method = segment.slice(0, parenIdx + 1);
  const rest = segment.slice(parenIdx + 1);

  // Find the matching closing paren
  let closingIdx = -1;
  let depth = 0;
  let inStr = false;
  let strCh = '';
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (inStr) { if (ch === strCh && rest[i - 1] !== '\\') inStr = false; continue; }
    if (ch === "'" || ch === '"') { inStr = true; strCh = ch; continue; }
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') {
      if (depth === 0) { closingIdx = i; break; }
      depth--;
    }
  }

  if (closingIdx === -1) return indent + segment;

  const inner = rest.slice(0, closingIdx);
  const after = rest.slice(closingIdx + 1);

  const args = splitAtTopLevelCommas(inner);
  if (args.length <= 1) return indent + segment;

  const formattedLines = [
    indent + method,
    ...args.map((arg, i) =>
      argIndent + arg.trim() + (i < args.length - 1 ? ',' : '')
    ),
    indent + ')' + after,
  ];

  return formattedLines.join('\n');
}

/**
 * Splits a string at commas that are at depth 0 (not inside parens/brackets/strings).
 */
function splitAtTopLevelCommas(str: string): string[] {
  const result: string[] = [];
  let current = '';
  let depth = 0;
  let inStr = false;
  let strCh = '';

  for (const ch of str) {
    if (inStr) { current += ch; if (ch === strCh) inStr = false; continue; }
    if (ch === "'" || ch === '"') { inStr = true; strCh = ch; current += ch; continue; }
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth--;
    if (depth === 0 && ch === ',') {
      result.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) result.push(current);
  return result;
}
